import { useCallback, useEffect, useState, type ReactElement } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { periodText } from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { ErrorState, Loading } from '../components/States.tsx';
import { documentUrl, openInNewTab } from '../document-viewer/open.ts';
import { loadFuelDockets, type FuelDocket, type PortalProject } from './api.ts';
import { TableWrap } from '../components/TableWrap.tsx';

export interface FuelDocketsListProps {
  readonly sb: SupabaseClient;
  readonly project: Pick<PortalProject, 'id' | 'number'>;
}

const n = (v: number): string => v.toLocaleString('en-AU', { maximumFractionDigits: 1 });

/** "4,000 L turbine (truck)", "4 drums turbine": what was delivered (decision 159). */
function upliftText(d: FuelDocket): string {
  const parts: string[] = [];
  if (d.upliftTruckTurbineL !== null) parts.push(`${n(d.upliftTruckTurbineL)} L turbine (truck)`);
  if (d.upliftTruckAvgasL !== null) parts.push(`${n(d.upliftTruckAvgasL)} L avgas (truck)`);
  if (d.upliftDrumsTurbine !== null) parts.push(`${n(d.upliftDrumsTurbine)} drums turbine`);
  if (d.upliftDrumsAvgas !== null) parts.push(`${n(d.upliftDrumsAvgas)} drums avgas`);
  return parts.join(', ');
}

const sizeText = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / 1024 / 1024).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} kB`;

const dateTime = (iso: string): string =>
  new Date(iso).toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

/**
 * Staff project page, "Fuel dockets" (docs/specs/cloud/storage-uploads.md §6): each resupply with a delivery docket
 * and the docket Teslyn Field uploaded, opened with a signed address of 5 minutes. Never shown to clients.
 */
export function FuelDocketsList({ sb, project }: FuelDocketsListProps): ReactElement {
  const [dockets, setDockets] = useState<FuelDocket[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    setError(null);
    setDockets(null);
    loadFuelDockets(sb, project.id)
      .then(setDockets)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, [sb, project.id]);
  useEffect(reload, [reload]);

  function open(d: FuelDocket): void {
    if (d.file) openInNewTab(documentUrl({ doc: 'fuel-docket', id: d.entryId }));
  }

  if (error) return <ErrorState message={error} onRetry={reload} />;
  if (!dockets) return <Loading what="fuel dockets" />;
  if (dockets.length === 0) {
    return (
      <div className="panel">
        <p className="body">
          No delivery dockets yet. When a crew adds a docket to a fuel resupply, Teslyn Field uploads it here
          after the resupply syncs.
        </p>
      </div>
    );
  }
  return (
    <div className="stack">
      <TableWrap>
        <table className="table" aria-label={`Fuel dockets of ${project.number}`}>
          <thead>
            <tr>
              <th className="label" scope="col">
                Resupply
              </th>
              <th className="label" scope="col">
                Location
              </th>
              <th className="label" scope="col">
                Delivered
              </th>
              <th className="label" scope="col">
                Docket
              </th>
              <th className="label" scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {dockets.map((d) => {
              const day = periodText(d.date);
              return (
                <tr key={d.entryId}>
                  <td>
                    <span>{day}</span> <span className="data caption">{d.time}</span>
                  </td>
                  <td>{d.location}</td>
                  <td className="data">{upliftText(d)}</td>
                  <td className="caption">
                    {d.file ? (
                      <>
                        <span className="data">{d.file.fileName}</span>
                        <br />
                        {`${sizeText(d.file.sizeBytes)} · ${
                          d.file.source === 'field' ? 'from Teslyn Field' : 'attached in Teslyn Cloud'
                        } · ${dateTime(d.file.uploadedAt)}`}
                      </>
                    ) : (
                      <span className="muted">Docket not uploaded yet</span>
                    )}
                  </td>
                  <td className="table__actions">
                    {d.file && (
                      <Button
                        variant="quiet"
                        aria-label={`Open docket of the resupply on ${day}`}
                        onClick={() => open(d)}
                      >
                        Open docket
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableWrap>
      <p className="caption muted">
        Photos in HEIC format download instead of opening; most browsers cannot show them.
      </p>
    </div>
  );
}
